/**
 * Orders — Order Confirmation & History Success Page.
 * Displays order details, tracking status, shipping address, and item summaries.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2,
  Package,
  Truck,
  MapPin,
  Clock,
  ArrowRight,
  Printer,
  ShoppingBag,
  ShieldCheck,
  ChevronRight,
  MessageCircle,
} from "lucide-react";
import { useCurrentUser } from "@/hooks/use-auth";
import {
  getUserOrders,
  getAllStoredOrders,
  fetchUserOrdersFromSupabase,
  fetchOrderById,
  type UserOrder,
} from "@/features/orders/store/orders-store";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { Reveal } from "@/components/common/reveal";

import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/orders")({
  head: () => ({
    meta: [
      { title: `Order Confirmation — ${siteConfig.name}` },
      {
        name: "description",
        content: "Thank you for your purchase. View your order details and delivery status.",
      },
      // Prerendered as static HTML (excluding it would make the Worker's SPA fallback
      // answer this URL with the homepage and a 200 — a soft 404, which is worse). But the
      // page is per-user and ~100 words, so it must not enter the index: robots.txt only
      // disallows /admin/ and /account/, and this URL is linked from the header on every
      // page, so a crawler reaches it. noindex,follow de-indexes the page while still
      // letting link equity flow through it. Deliberately noindex rather than a robots.txt
      // Disallow: a disallowed URL is never fetched, so the directive would never be read
      // and the URL could still be indexed from its internal links alone.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: OrdersPage,
});

function OrdersPage() {
  const { user } = useCurrentUser();
  const [orders, setOrders] = useState<UserOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupSearching, setLookupSearching] = useState(false);

  const handleLookupOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = lookupQuery.trim();
    if (!trimmed) return;
    setLookupSearching(true);
    setLookupError(null);
    try {
      const order = await fetchOrderById(trimmed);
      if (order) {
        setOrders([order]);
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem("recent_order_id", order.id);
        }
        setLookupQuery("");
      } else {
        setLookupError("No order found matching that reference number. Please check the ID and try again.");
      }
    } catch {
      setLookupError("Failed to query order. Please check your internet connection.");
    } finally {
      setLookupSearching(false);
    }
  };

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    const sessionOrderId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem("recent_order_id") : null;

    let activeOrders: UserOrder[] = [];

    if (user) {
      const userOrds = getUserOrders(user.id, user.email, user.phone);
      const stored = getAllStoredOrders();
      const combinedMap = new Map<string, UserOrder>();
      
      // Include any orders stored in this browser
      stored.forEach((o) => combinedMap.set(o.id, o));
      userOrds.forEach((o) => combinedMap.set(o.id, o));

      if (sessionOrderId && !combinedMap.has(sessionOrderId)) {
        const found = await fetchOrderById(sessionOrderId);
        if (found) combinedMap.set(found.id, found);
      }

      activeOrders = Array.from(combinedMap.values());
      if (activeOrders.length > 0) {
        setOrders(activeOrders);
      }

      fetchUserOrdersFromSupabase(user.id, user.email).then((remoteOrders) => {
        if (remoteOrders && remoteOrders.length > 0) {
          setOrders((prev) => {
            const merged = new Map<string, UserOrder>();
            remoteOrders.forEach((o) => merged.set(o.id, o));
            prev.forEach((o) => {
              if (!merged.has(o.id)) merged.set(o.id, o);
            });
            stored.forEach((o) => {
              if (!merged.has(o.id)) merged.set(o.id, o);
            });
            return Array.from(merged.values()).sort((a, b) => b.id.localeCompare(a.id));
          });
        }
      });
    } else {
      // Unauthenticated / guest users
      const stored = getAllStoredOrders();
      if (sessionOrderId) {
        const matching = stored.filter((o) => o.id === sessionOrderId);
        if (matching.length > 0) {
          const others = stored.filter((o) => o.id !== sessionOrderId);
          activeOrders = [...matching, ...others];
          setOrders(activeOrders);
        } else {
          setLoading(true);
          const remoteOrder = await fetchOrderById(sessionOrderId, true);
          setLoading(false);
          if (remoteOrder) {
            const others = stored.filter((o) => o.id !== remoteOrder.id);
            activeOrders = [remoteOrder, ...others];
            setOrders(activeOrders);
          } else if (stored.length > 0) {
            activeOrders = stored;
            setOrders(stored);
          } else {
            setOrders([]);
          }
        }
      } else if (stored.length > 0) {
        activeOrders = stored;
        setOrders(stored);
      } else {
        setOrders([]);
      }
    }

    // Background fresh status revalidation from server for active orders.
    // If admin updated the status in database, this ensures customer sees the fresh status!
    if (activeOrders.length > 0) {
      const idsToRefresh = activeOrders.slice(0, 5).map((o) => o.id);
      for (const id of idsToRefresh) {
        fetchOrderById(id, true).then((fresh) => {
          if (fresh) {
            setOrders((prev) =>
              prev.map((o) => (o.id === fresh.id ? { ...o, ...fresh } : o))
            );
          }
        });
      }
    }
  }, [user]);

  useEffect(() => {
    loadOrders();

    const handleUpdate = () => {
      loadOrders();
    };
    window.addEventListener("orders_updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("orders_sync");
      bc.onmessage = () => {
        loadOrders();
      };
    } catch {
      // BroadcastChannel unsupported
    }

    let channel: any = null;
    if (user) {
      const channelName = `orders-confirm-${Math.random().toString(36).substring(2, 9)}`;
      channel = supabase
        .channel(channelName)
        .on("postgres_changes", { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${user.id}` }, () => {
          loadOrders();
        })
        .subscribe();
    }

    return () => {
      window.removeEventListener("orders_updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
      if (bc) bc.close();
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, loadOrders]);

  const currentOrder =
    (selectedOrderId ? orders.find((o) => o.id === selectedOrderId) : null) ||
    (orders.length > 0 ? orders[0] : null);
  const latestOrder = currentOrder;

  const statusLower = (latestOrder?.status || "Processing").toLowerCase();
  const isCancelled = statusLower === "cancelled";
  const isDelivered = statusLower === "delivered";
  const isShipped = statusLower === "shipped" || isDelivered;
  const isProcessing = statusLower === "processing" || isShipped;

  return (
    <div className="section-container pb-28 pt-24 md:pt-32 min-h-[75vh] print:pt-4 print:pb-4">
      {/* ── Print-only styles ─────────────────────────────────────────────────
          Hides nav, buttons, decorative blobs and resets layout to a compact
          single-page invoice. Uses a <style> tag so it co-locates with this
          component and doesn't require a separate CSS file. */}
      <style>{`
        @media print {
          html, body {
            background-color: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Ensure images print */
          img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>
      <Reveal>
        <div className="max-w-4xl mx-auto print:hidden">
          {/* Breadcrumb — hidden when printing */}
          <nav className="mb-6 flex items-center gap-2 text-xs text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-stone-900 font-semibold">Order Confirmation</span>
          </nav>

          {/* Multiple Orders Switcher Bar (Visible when user has placed multiple orders) */}
          {orders.length > 1 && (
            <div className="mb-6 bg-white rounded-2xl p-4 border border-stone-200 shadow-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-600 flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5 text-emerald-800" />
                  Your Placed Orders ({orders.length})
                </span>
                <span className="text-[11px] text-stone-400">Tap an order to view its invoice and tracking</span>
              </div>
              <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none">
                {orders.map((ord) => {
                  const isActive = ord.id === latestOrder?.id;
                  const sLower = (ord.status || "").toLowerCase();
                  return (
                    <button
                      key={ord.id}
                      type="button"
                      onClick={() => {
                        setSelectedOrderId(ord.id);
                        if (typeof sessionStorage !== "undefined") {
                          sessionStorage.setItem("recent_order_id", ord.id);
                        }
                      }}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-xs font-medium transition-all shrink-0 text-left ${
                        isActive
                          ? "border-emerald-800 bg-emerald-50 text-emerald-950 font-bold shadow-xs ring-1 ring-emerald-800/20"
                          : "border-stone-200 bg-stone-50/60 hover:bg-stone-100 text-stone-700"
                      }`}
                    >
                      <Package className={`h-4 w-4 shrink-0 ${isActive ? "text-emerald-800" : "text-stone-400"}`} />
                      <div>
                        <div className="font-mono text-[11px] font-bold">{ord.id}</div>
                        <div className="text-[10px] text-stone-500 font-normal">{ord.date} • {formatPrice(ord.total)}</div>
                      </div>
                      <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                        sLower === "delivered"
                          ? "bg-emerald-100 text-emerald-800"
                          : sLower === "shipped"
                          ? "bg-blue-100 text-blue-800"
                          : sLower === "cancelled"
                          ? "bg-rose-100 text-rose-800"
                          : "bg-amber-100 text-amber-800"
                      }`}>
                        {ord.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {latestOrder ? (
            <div className="space-y-8 print-outer">
              {/* Order Success Hero Card */}
              <div className="print-hero bg-emerald-950 text-white rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
                <div className="print-blob absolute top-0 right-0 -mr-10 -mt-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30 shadow-inner">
                      <CheckCircle2 className="h-8 w-8 sm:h-10 sm:w-10" />
                    </div>
                    <div>
                      <span className={`inline-block px-3 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-1 border ${
                        isCancelled
                          ? "bg-rose-500/20 text-rose-300 border-rose-500/30"
                          : isDelivered
                          ? "bg-emerald-500/30 text-emerald-200 border-emerald-400/40"
                          : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                      }`}>
                        {isCancelled ? "Order Cancelled" : isDelivered ? "Delivered Successfully" : isShipped ? "Dispatched / In Transit" : "Payment Confirmed"}
                      </span>
                      <h1 className="font-display font-bold text-2xl sm:text-4xl tracking-tight">
                        {isCancelled
                          ? "Order Cancelled"
                          : isDelivered
                          ? "Order Delivered!"
                          : isShipped
                          ? "Your Order is on the way!"
                          : "Thank you for your order!"}
                      </h1>
                      <p className="text-stone-300 text-xs sm:text-sm mt-1">
                        {isCancelled
                          ? "This order was cancelled. Any charged amount will be refunded to your source payment method."
                          : isDelivered
                          ? "Your botanical pieces have been successfully delivered to your doorstep."
                          : isShipped
                          ? "Your package has been dispatched by our logistics partner and is in transit."
                          : "We've received your order and our artisans are preparing your botanical pieces."}
                      </p>
                    </div>
                  </div>

                  <div className="bg-emerald-900/60 p-4 rounded-2xl border border-emerald-800 shrink-0 text-left sm:text-right w-full sm:w-auto">
                    <p className="text-[10px] text-emerald-300 uppercase tracking-widest font-bold">
                      Order Number
                    </p>
                    <p className="font-mono text-lg font-bold text-white mt-0.5">
                      {latestOrder.id}
                    </p>
                    <p className="text-[11px] text-emerald-300/80 mt-1">{latestOrder.date}</p>
                  </div>
                </div>

                {/* Delivery Progress Bar - Dynamically reflects Admin updates */}
                {!isCancelled ? (
                  <div className="mt-8 pt-8 border-t border-emerald-900/80 grid grid-cols-4 gap-2 text-center text-[10px] sm:text-xs">
                    <div className="flex flex-col items-center gap-2 text-emerald-300 font-bold">
                      <div className="h-7 w-7 rounded-full bg-emerald-500 text-emerald-950 flex items-center justify-center font-bold text-xs shadow-md">
                        ✓
                      </div>
                      <span>Order Placed</span>
                    </div>
                    <div className={`flex flex-col items-center gap-2 ${isProcessing ? "text-emerald-200 font-bold" : "text-emerald-400/60 font-medium"}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center font-bold text-xs ${
                        isProcessing
                          ? "bg-emerald-500 text-emerald-950 shadow-md"
                          : "bg-emerald-900/50 text-emerald-500 border border-emerald-800"
                      }`}>
                        {isProcessing ? "✓" : "2"}
                      </div>
                      <span>Processing</span>
                    </div>
                    <div className={`flex flex-col items-center gap-2 ${isShipped ? "text-emerald-200 font-bold" : "text-emerald-400/60 font-medium"}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center font-bold text-xs ${
                        isShipped
                          ? "bg-emerald-500 text-emerald-950 shadow-md"
                          : "bg-emerald-900/50 text-emerald-500 border border-emerald-800"
                      }`}>
                        {isShipped ? "✓" : "3"}
                      </div>
                      <span>Shipped</span>
                    </div>
                    <div className={`flex flex-col items-center gap-2 ${isDelivered ? "text-emerald-200 font-bold" : "text-emerald-400/60 font-medium"}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center font-bold text-xs ${
                        isDelivered
                          ? "bg-emerald-500 text-emerald-950 shadow-md"
                          : "bg-emerald-900/50 text-emerald-500 border border-emerald-800"
                      }`}>
                        {isDelivered ? "✓" : "4"}
                      </div>
                      <span>Delivered</span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 p-4 rounded-2xl bg-rose-950/60 border border-rose-800/80 text-rose-200 text-xs">
                    Status: <strong>Order Cancelled</strong>. If you did not request this cancellation, please contact our support team.
                  </div>
                )}

                {/* Courier & AWB Details Banner if Shipped */}
                {(latestOrder.courierName || latestOrder.awbNumber) && (
                  <div className="mt-5 p-3.5 bg-emerald-900/70 rounded-2xl border border-emerald-700/60 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <span className="text-emerald-200 font-medium">
                      Courier Partner: <strong className="text-white font-bold">{latestOrder.courierName || "Express Courier"}</strong>
                    </span>
                    {latestOrder.awbNumber && (
                      <span className="text-emerald-200 font-medium">
                        AWB Tracking: <strong className="text-white font-mono font-bold bg-emerald-950 px-2 py-0.5 rounded border border-emerald-700">{latestOrder.awbNumber}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Order Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 print-grid">
                {/* Left: Items Summary */}
                <div className="print-card md:col-span-7 bg-white rounded-3xl p-6 sm:p-8 border border-stone-200 shadow-xs space-y-6">
                  <div className="flex items-center justify-between border-b pb-4">
                    <h2 className="font-display font-bold text-lg text-stone-900 flex items-center gap-2">
                      <Package className="h-5 w-5 text-emerald-800" /> Order Summary
                    </h2>
                    <span className="text-xs font-semibold text-stone-500">
                      {latestOrder.items.length} Item(s)
                    </span>
                  </div>

                  <div className="divide-y divide-stone-100">
                    {latestOrder.items.map((item) => (
                      <div key={item.id} className="py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="h-16 w-14 object-cover rounded-xl bg-stone-100 border border-stone-200 shrink-0"
                            />
                          )}
                          <div>
                            <h3 className="font-bold text-stone-900 text-sm line-clamp-1">
                              {item.name}
                            </h3>
                            <p className="text-xs text-stone-500 mt-0.5">Quantity: {item.qty}</p>
                          </div>
                        </div>
                        <span className="font-bold text-stone-900 tabular-nums text-sm">
                          {formatPrice(item.price * item.qty)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-stone-200 pt-4 space-y-2 text-xs text-stone-600">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="tabular-nums font-semibold text-stone-900">
                        {formatPrice(latestOrder.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shipping</span>
                      <span className="text-emerald-700 font-bold">
                        {latestOrder.shippingCost === 0 ? "Complimentary Free Delivery" : formatPrice(latestOrder.shippingCost)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST (18%)</span>
                      <span className="tabular-nums font-semibold text-stone-900">
                        {formatPrice(latestOrder.gst || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-3 text-base font-bold text-stone-900">
                      <span>Total Amount Paid</span>
                      <span className="text-emerald-900 font-display text-lg tabular-nums">
                        {formatPrice(latestOrder.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Shipping Address & Actions */}
                <div className="md:col-span-5 space-y-6">
                  {/* Delivery Address Card */}
                  <div className="print-card bg-stone-50 rounded-3xl p-6 border border-stone-200 space-y-4">
                    <h3 className="font-display font-bold text-sm text-stone-900 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-emerald-800" /> Delivery Address
                    </h3>

                    {latestOrder.shippingAddress ? (
                      <div className="text-xs text-stone-700 leading-relaxed space-y-1 bg-white p-4 rounded-2xl border border-stone-200/80">
                        <p className="font-bold text-stone-900 text-sm">
                          {latestOrder.shippingAddress.fullName}
                        </p>
                        <p>{latestOrder.shippingAddress.address}</p>
                        <p>
                          {latestOrder.shippingAddress.city}, {latestOrder.shippingAddress.state} -{" "}
                          {latestOrder.shippingAddress.pincode}
                        </p>
                        <p className="pt-1 text-stone-500 font-medium">
                          Phone: {latestOrder.shippingAddress.phone}
                        </p>
                      </div>
                    ) : (
                      <div className="text-xs text-stone-600">
                        <p className="font-medium">{latestOrder.userEmail}</p>
                        <p>{latestOrder.userPhone}</p>
                      </div>
                    )}

                    <div className="pt-2 flex items-center gap-2 text-xs text-emerald-800 font-semibold">
                      <Truck className="h-4 w-4" /> Expected Arrival: 3–5 Business Days
                    </div>
                  </div>

                  {/* Payment Method Card */}
                  <div className="print-card bg-stone-50 rounded-3xl p-6 border border-stone-200 space-y-3">
                    <h3 className="font-display font-bold text-sm text-stone-900 flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-800" /> Payment Details
                    </h3>
                    <p className="text-xs text-stone-700 font-medium bg-white p-3 rounded-xl border border-stone-200">
                      {latestOrder.paymentMethod || "Paid via Razorpay 1-Click"}
                    </p>
                  </div>

                  {/* Action Buttons — hidden when printing */}
                  <div className="space-y-3 pt-2" data-print="hide">
                    {/* 1-Click WhatsApp Order Support / Tracking */}
                    <a
                      href={`https://wa.me/919344580982?text=${encodeURIComponent(
                        `Hello Aster Decorz Team! 👋\nI have placed order *${latestOrder.id}* on ${latestOrder.date} for ${formatPrice(latestOrder.total)}.\nCould you please share my order status and tracking details?`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-h-12 w-full items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-md"
                    >
                      <MessageCircle className="h-4 w-4" /> Track via WhatsApp
                    </a>

                    <button
                      onClick={() => window.print()}
                      className="flex min-h-12 w-full items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs uppercase tracking-wider rounded-2xl transition-colors border border-stone-300"
                    >
                      <Printer className="h-4 w-4" /> Print / Save Invoice
                    </button>

                    <Link
                      to="/shop"
                      className="flex min-h-12 w-full items-center justify-center gap-2 bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs uppercase tracking-wider rounded-2xl transition-[color,background-color,border-color,box-shadow,transform,opacity] shadow-md"
                    >
                      <ShoppingBag className="h-4 w-4" /> Continue Shopping
                    </Link>

                    <button
                      type="button"
                      onClick={() => {
                        setOrders([]);
                        if (typeof sessionStorage !== "undefined") {
                          sessionStorage.removeItem("recent_order_id");
                        }
                      }}
                      className="text-xs text-stone-500 hover:text-emerald-800 underline transition-colors pt-2 block text-center w-full"
                    >
                      Track a different order
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-stone-200 p-8 shadow-xs">
              <div className="h-10 w-10 rounded-full border-2 border-emerald-900 border-t-transparent animate-spin mx-auto mb-4" />
              <p className="font-display font-medium text-base text-stone-900">Retrieving your order details…</p>
            </div>
          ) : (
            /* Order Lookup & No Orders Fallback */
            <div className="text-center py-16 bg-white rounded-3xl border border-stone-200 p-8 shadow-xs max-w-xl mx-auto">
              <div className="h-16 w-16 bg-emerald-50 text-emerald-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100">
                <Package className="h-8 w-8" />
              </div>
              <h2 className="font-display font-bold text-2xl text-stone-900">Track Your Order</h2>
              <p className="text-xs text-stone-500 mt-1 max-w-md mx-auto">
                Enter your Order Reference Number below (e.g. VRD-...) to check delivery status, view receipt, or get WhatsApp support.
              </p>

              <form onSubmit={handleLookupOrder} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                <input
                  type="text"
                  value={lookupQuery}
                  onChange={(e) => {
                    setLookupQuery(e.target.value);
                    if (lookupError) setLookupError(null);
                  }}
                  placeholder="e.g. VRD-261003-KYBB81YM"
                  className="flex-1 rounded-2xl border border-stone-300 px-4 py-3 text-xs font-mono uppercase focus:border-emerald-800 focus:outline-none bg-stone-50/50"
                />
                <button
                  type="submit"
                  disabled={lookupSearching || !lookupQuery.trim()}
                  className="inline-flex items-center justify-center gap-2 bg-emerald-900 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white rounded-2xl hover:bg-emerald-800 transition-colors disabled:opacity-50 shrink-0 shadow-sm"
                >
                  {lookupSearching ? "Searching…" : "Track"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </form>

              {lookupError && (
                <p className="mt-3 text-xs text-rose-600 font-medium">{lookupError}</p>
              )}

              <div className="mt-8 pt-6 border-t border-stone-100">
                <Link
                  to="/shop"
                  className="inline-flex min-h-11 items-center justify-center gap-2 bg-stone-100 px-6 text-xs font-bold uppercase tracking-wider text-stone-800 rounded-2xl hover:bg-stone-200 transition-colors"
                >
                  <ShoppingBag className="h-4 w-4" />
                  <span>Browse Plant Catalogue</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </Reveal>

      {/* Professional Invoice (Print Only) */}
      {latestOrder && (
        <div className="hidden print:block w-full max-w-4xl mx-auto bg-white text-stone-900 p-8">
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-stone-800 pb-8 mb-8">
            <div>
              <h1 className="font-display font-bold text-4xl tracking-tight text-emerald-900">INVOICE</h1>
              <p className="text-sm text-stone-500 font-medium mt-1">Receipt / Proof of Purchase</p>
            </div>
            <div className="text-right">
              <h2 className="font-display font-bold text-2xl text-stone-900">{siteConfig.name}</h2>
              <p className="text-sm text-stone-600 mt-1">Luxury Faux Botanicals</p>
            </div>
          </div>

          {/* Details Row */}
          <div className="flex justify-between mb-12">
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-1">Invoice To</h3>
                {latestOrder.shippingAddress ? (
                  <div className="text-sm font-medium leading-relaxed">
                    <p className="font-bold text-stone-900 text-base mb-1">{latestOrder.shippingAddress.fullName}</p>
                    <p>{latestOrder.shippingAddress.address}</p>
                    <p>{latestOrder.shippingAddress.city}, {latestOrder.shippingAddress.state} {latestOrder.shippingAddress.pincode}</p>
                    <p className="mt-1">Tel: {latestOrder.shippingAddress.phone}</p>
                  </div>
                ) : (
                  <div className="text-sm font-medium leading-relaxed">
                    <p>{latestOrder.userEmail}</p>
                    <p>{latestOrder.userPhone}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4 text-right">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-1">Order Number</h3>
                <p className="font-mono font-bold text-sm bg-stone-100 px-3 py-1 rounded inline-block">{latestOrder.id}</p>
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-1">Date of Issue</h3>
                <p className="font-medium text-sm">
                  {new Date(latestOrder.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-1">Payment Method</h3>
                <p className="font-medium text-sm">{latestOrder.paymentMethod || "Paid via Razorpay"}</p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <table className="w-full text-left mb-12">
            <thead>
              <tr className="border-b-2 border-stone-200">
                <th className="py-3 font-bold text-xs uppercase tracking-widest text-stone-500">Item Description</th>
                <th className="py-3 font-bold text-xs uppercase tracking-widest text-stone-500 text-center w-24">Qty</th>
                <th className="py-3 font-bold text-xs uppercase tracking-widest text-stone-500 text-right w-32">Price</th>
                <th className="py-3 font-bold text-xs uppercase tracking-widest text-stone-500 text-right w-32">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {latestOrder.items.map((item) => (
                <tr key={item.id}>
                  <td className="py-4 text-sm font-semibold text-stone-900">{item.name}</td>
                  <td className="py-4 text-sm font-medium text-stone-600 text-center">{item.qty}</td>
                  <td className="py-4 text-sm font-medium text-stone-600 tabular-nums text-right">{formatPrice(item.price)}</td>
                  <td className="py-4 text-sm font-bold text-stone-900 tabular-nums text-right">{formatPrice(item.price * item.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-3">
              <div className="flex justify-between text-sm text-stone-600">
                <span>Subtotal</span>
                <span className="tabular-nums font-medium">{formatPrice(latestOrder.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-stone-600">
                <span>Shipping</span>
                <span className="tabular-nums font-medium">
                  {latestOrder.shippingCost === 0 ? "Free" : formatPrice(latestOrder.shippingCost)}
                </span>
              </div>
              <div className="flex justify-between text-sm text-stone-600 pb-3 border-b border-stone-200">
                <span>GST (18%)</span>
                <span className="tabular-nums font-medium">{formatPrice(latestOrder.gst || 0)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-emerald-900 pt-1">
                <span>Total Amount</span>
                <span className="tabular-nums">{formatPrice(latestOrder.total)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-20 pt-8 border-t border-stone-200 text-center text-xs text-stone-500 space-y-1">
            <p className="font-bold text-stone-700">Thank you for your business!</p>
            <p>If you have any questions about this invoice, please contact support@{siteConfig.name.toLowerCase().replace(/\s+/g, "")}.com</p>
          </div>
        </div>
      )}
    </div>
  );
}
